import React from 'react'
import PropTypes from 'prop-types'
import { css } from '@linaria/core'
import { Paragraph, WhiteTheme, useContainerWidth, colors } from '@fs/zion-ui'
import { MediaFooter } from '../MemoryCard/components'

const collageCss = css`
  position: relative; // needed for absolutely positioned MediaFooter when size is 'sm'
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-grow: 1;
  align-items: stretch;
  min-height: 200px;
`
const image1Css = css`
  flex-grow: 2;
`
const image2Css = css`
  flex-grow: 1;
`
const image3Css = css`
  margin-top: 4px;
  flex-grow: 1;
`
const imageOverlayCss = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: absolute;
  top: 4px;
  inset-inline-start: 0;
  inset-inline-end: 0;
  bottom: 0;
`
const overlayContainerCss = css`
  display: flex;
  flex-grow: 1;
  position: relative;
`
const countTextCss = css`
  background: ${colors.gray80};
  color: ${colors.gray02};
  padding: 6px 8px;
  border-radius: 6px;
`
const rightSideFlex = css`
  margin-inline-start: 4px;
  display: flex;
  flex-direction: column;
  &[image-count='2'] {
    flex: 2;
  }
  &[image-count='3'] {
    flex: 1;
  }
`
const loadingErrorCss = css`
  position: absolute;
  height: 100%;
  width: 100%;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`

export default function MemoryCollage({
  images,
  height,
  minHeight = '200px',
  maxHeight,
  size,
  alt,
  imagesWithLoadingErrors = [],
  loadingErrorText,
  ...props
}) {
  const atWidth = useContainerWidth()
  const collageImageCss = [image1Css, image2Css, image3Css]
  const computedSize = size || atWidth({ default: 'sm', nano: 'lg' })
  const isSmall = computedSize === 'sm'
  const displayedImages = isSmall ? images.slice(0, 1) : images.slice(0, 3)

  const generateCollageItem = (src, i) => {
    const loadingError = imagesWithLoadingErrors.includes(src)
    const style = {
      background: colors.gray90,
      position: 'relative',
    }
    if (!loadingError) {
      style.background = `url('${src}') center center/cover no-repeat`
    }
    return (
      <div
        className={collageImageCss[i]}
        aria-label={displayedImages.length === 1 ? alt : undefined}
        key={src}
        style={style}
        {...props}
      >
        {loadingError && (i < 2 || images.length === 3) && (
          <WhiteTheme className={loadingErrorCss}>
            <Paragraph size="sm" secondary centered>
              {loadingErrorText}
            </Paragraph>
          </WhiteTheme>
        )}
      </div>
    )
  }

  return (
    <div
      className={collageCss}
      image-count={displayedImages.length}
      style={{ height, minHeight, maxHeight }}
      data-testid="memory-collage"
    >
      {generateCollageItem(displayedImages[0], 0)}
      {displayedImages.length > 1 && (
        <div className={rightSideFlex} image-count={displayedImages.length}>
          {displayedImages.slice(1).map((item, i) => {
            if (i === 1 && images.length > 3 && !isSmall) {
              return (
                <div className={overlayContainerCss} key={item}>
                  {generateCollageItem(item, i + 1)}
                  <div className={imageOverlayCss}>
                    <Paragraph size="sm">
                      <strong className={countTextCss}>+{images.length - 3}</strong>
                    </Paragraph>
                  </div>
                </div>
              )
            }
            return generateCollageItem(item, i + 1)
          })}
        </div>
      )}
      {isSmall && images.length > 1 && <MediaFooter text={`+${images.length - 1}`} alignX="end" bold />}
    </div>
  )
}

MemoryCollage.propTypes = {
  /** Array of image urls used in the collage. */
  images: PropTypes.arrayOf(PropTypes.string).isRequired,

  /** Sets the `height` of the collage. */
  height: PropTypes.string,

  /** Sets the `min-height` of the collage. */
  minHeight: PropTypes.string,

  /** Sets the `max-height` of the collage. */
  maxHeight: PropTypes.string,

  /** Overrides the size that is calculated from useContainerWidth to determine the number of images to display in the collage. */
  size: PropTypes.string,

  /** Alt text for single image, and also used as `aria-label` for collage. */
  alt: PropTypes.string,

  /** Array of image urls used in Collage that have loading errors. */
  imagesWithLoadingErrors: PropTypes.arrayOf(PropTypes.string),

  /** Error text to display for any images with loading errors. */
  loadingErrorText: PropTypes.string,
}
